import { useCollection } from "@hooks/useCollection";
import { useHelper } from "@hooks/useHelper";
import { useSale } from "@hooks/useSale";
import {
  ConfigFilterOption,
  ConfigSort,
  useFilters,
  useSortBy,
} from "@usereactify/search";
import React, {
  Context,
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useMemo,
  useState,
} from "react";

import { useSite } from "~/hooks/useSite";

type FilterProviderProps = {
  children: ReactNode;
  handle: string;
};

export type FilterContext = {
  gender: "men" | "women";
  filters: ConfigFilterOption[];
  filtersSelected: {
    handle: string;
    value:
      | string
      | string[]
      | {
          label: string;
          start: number;
          end: number;
        }[];
  }[];
  sortOption: ConfigSort | undefined;
  sortOptions: ConfigSort[];
  setSortOption: (handle: string) => void;
  setShowMore: Dispatch<SetStateAction<boolean>>;
  handleScroll: () => void;
  showMore: boolean;
  showViewToggle: boolean;
};

const getActiveFilters = (
  filters: ConfigFilterOption[],
  isSale: boolean,
  hideSizeFilter: boolean,
  currencyCode: string | undefined,
  collectionTitle: string,
) =>
  filters?.filter((filter) => {
    if (hideSizeFilter && filter.handle === "size") {
      return false;
    }

    if (collectionTitle?.includes("Shoe Care") && filter.handle == "colour") {
      return false;
    }

    if (
      collectionTitle?.includes("Shoe Care") &&
      filter.handle == "subcategory"
    ) {
      return false;
    }

    if (filter.handle === "spring-sale") {
      return false;
    }

    if (isSale && filter.handle === `sale-price-${currencyCode}`) {
      return true;
    }

    if (!isSale && filter.handle === "discount") {
      return false;
    }

    if (
      isSale &&
      (filter.handle.startsWith("sale-price") || filter.handle === "price")
    ) {
      return false;
    }

    if (!isSale && filter.handle.startsWith("sale-")) {
      return false;
    }

    return true;
  });

const getSortOptions = (
  sortOptions: ConfigSort[],
  isSale: boolean,
  currencyCode: string,
): ConfigSort[] => {
  let activeSortOptions: ConfigSort[] = [];

  const updatedSortOptions = sortOptions.map((option) => {
    const name = option?.name?.split("[")[0].trim();
    return { ...option, name };
  });

  if (isSale) {
    activeSortOptions = updatedSortOptions.filter((option) => {
      if (
        option.field.includes("price") &&
        !option.field.includes(`tags_sale_price_${currencyCode?.toLowerCase()}`)
      ) {
        return false;
      }
      return true;
    });
  } else {
    activeSortOptions = updatedSortOptions.filter((option) => {
      if (option.field.startsWith("tags_sale_price_")) {
        return false;
      }
      return true;
    });
  }

  return activeSortOptions?.sort((a, b) => (a.position < b.position ? -1 : 1));
};

export const FilterContext: Context<FilterContext> =
  createContext<FilterContext>({
    gender: "women",
    filters: [],
    filtersSelected: [],
    sortOption: undefined,
    sortOptions: [],
    setSortOption: () => {},
    setShowMore: () => {},
    showMore: false,
    handleScroll: () => {},
    showViewToggle: true,
  });

const shouldShowViewToggle = (collectionTitle: string) => {
  // Return false for specific collections where we don't want to show the toggle
  return !(
    collectionTitle?.includes("Shoe Care") ||
    collectionTitle?.includes("Pet") ||
    collectionTitle?.includes("Socks & Accessories")
  );
};

export const FilterProvider = ({ children, handle }: FilterProviderProps) => {
  const site = useSite();
  const { filters: reactifyFilters, selected: filtersSelected } = useFilters();
  const { isSaleActive } = useSale();
  const isSale = isSaleActive();
  const { scrollTo } = useHelper();
  const { allCollections } = useCollection();
  const {
    sortOption,
    sortOptions: reactifySortOptions,
    setSortOption,
  } = useSortBy();
  const [showMore, setShowMore] = useState(false);
  const currentCollection = allCollections?.edges?.find(
    (collection: any) => handle === collection?.node?.shopify?.handle,
  );
  const gender = currentCollection?.node?.gender;
  const hideSizeFilter = currentCollection?.node?.hideSizeFilter;
  const collectionTitle = currentCollection?.node.title;
  const currencyCode = site.currencyCode;
  const handleScroll = () => {
    scrollTo(0, null, 1000);
  };
  const filters = useMemo(
    () =>
      getActiveFilters(
        reactifyFilters || [],
        isSale,
        hideSizeFilter,
        currencyCode,
        collectionTitle,
      ),
    [reactifyFilters, isSale, hideSizeFilter, currencyCode, collectionTitle],
  );
  const sortOptions = useMemo(
    () => getSortOptions(reactifySortOptions || [], isSale, currencyCode ?? ""),
    [reactifySortOptions, isSale, currencyCode],
  );

  const values = useMemo(
    () => ({
      gender,
      filters: filters || [],
      filtersSelected: filtersSelected || [],
      sortOption,
      sortOptions,
      setSortOption,
      setShowMore,
      showMore,
      handleScroll,
      showViewToggle: shouldShowViewToggle(collectionTitle),
    }),
    [
      gender,
      filters,
      filtersSelected,
      sortOption,
      sortOptions,
      setSortOption,
      setShowMore,
      showMore,
      handleScroll,
      collectionTitle,
    ],
  );

  return (
    <FilterContext.Provider value={values}>{children}</FilterContext.Provider>
  );
};
